import axios from "axios";
import _ from "lodash";
import { Dispatch } from "redux";

import { logoutAction } from "containers/authentication/redux/actions";
import { backgroundOperationStarted } from "containers/background-operations/redux/backgroundOperationStarted";
import { showNotificationActionCreator } from "containers/notifications/actionCreator";
import * as levels from "containers/notifications/actionLevels";
import * as notificationTypes from "containers/notifications/actionTypes";
import { BUSINESS_ERROR } from "model/application/Api";
import TLang from "model/application/Lang";
import {
  closeWarningModalActionCreator,
  openWarningModalActionCreator,
} from "redux/actionCreators/appActionCreator";
import { TAny } from "utils/types";
import { formatSQLString } from "utils/utils";

import { ajaxErrorAction } from "../actionCreators/ajaxActionCreator";
import {
  // BusinessError,
  AlreadyExistsBusinessError,
  AuthenticationBusinessError,
  FreeTrialLimitReachedError,
  MockAxiosError,
  NotAllowedBusinessError,
  NotFoundBusinessError,
  NotUpToDateBusinessError,
  PartialFailureBusinessError,
  PayloadTooLargeBusinessError,
  QuotaExceededBusinessError,
  RunInBackgroundBusinessError,
  SQLBusinessError,
  TErrorDetails,
  TimeoutError,
  ValidationBusinessError,
} from "./actionErrors";

export type TreatErrorNotificationFunc = (
  dispatch: Dispatch,
  actionName: string,
  error: any,
  failureActionCreatorFunction: ((error: any, objectId?: string) => any) | null, // sometimes we don't need to dispatch a failure action
  lang: TLang,
  autologout?: boolean,
  objectId?: string,
) => void;

export const treatErrorNotification: TreatErrorNotificationFunc = (
  dispatch,
  actionName,
  error,
  failureActionCreatorFunction,
  lang,
  autologout = true,
  objectId,
) => {
  if (isCancelledRequest(error)) {
    // A cancelled / aborted request raises nothing for the user - no error
    // action, no notification. Its failure action still has to go through: it
    // is what brings the loading flag it raised back down, and a logout
    // cancels every request in flight. Skipping it left the next session
    // saying "Loading..." for ever (FP-13488).
    // Also covers Axios 1.x CanceledError / ERR_CANCELED and browser "Request
    // aborted" (no HTTP response) which used to surface as a false network toast
    // (FP-13797).
    if (failureActionCreatorFunction) {
      dispatch(failureActionCreatorFunction(error, objectId));
    }
    return;
  }

  // Special case for errors we don't want to catch in tests
  if (error instanceof MockAxiosError) {
    throw error;
  }

  /**
  const message = getErrorMessage(error, lang);

  let detailedMessage = `Error in action ${actionName}: `;
  if (error.message) {
    detailedMessage = `${detailedMessage}${error.message}${error?.response?.data?.data?.error?.message}`;
  }
  */

  const message = getErrorMessage(error, lang);
  const detailedMessage = getDetailedErrorMessage(error, actionName);

  if (
    (error.errorStatus === BUSINESS_ERROR.AUTHENTICATION ||
      _.includes(error?.message, "client with id") ||
      _.includes(error?.message, "not having access to any client") ||
      _.includes(error?.message, "token expired")) &&
    autologout
  ) {
    logoutAction()(dispatch);
    return;
  }

  dispatch(ajaxErrorAction(error, actionName));

  if (failureActionCreatorFunction) {
    dispatch(failureActionCreatorFunction(error, objectId));
  }

  let notificationAction = showNotificationActionCreator(
    notificationTypes.NOTIFICATION_ERROR,
    levels.NOTIFICATION_LEVEL_ERROR,
    message,
    detailedMessage,
  );

  if (error.errorStatus === BUSINESS_ERROR.RUN_IN_BACKGROUND) {
    notificationAction = showNotificationActionCreator(
      notificationTypes.NOTIFICATION_SUCCESS,
      levels.NOTIFICATION_LEVEL_SUCCESS,
      message,
    );
    trackStartedBackgroundOperation(dispatch, error.details);
  }

  dispatch(notificationAction);
};

/**
 * Records a background operation the backend just kicked off, so screens can
 * follow it without waiting for a full refetch. Older backends do not send the
 * operation id, hence the guard.
 */
const trackStartedBackgroundOperation = (
  dispatch: Dispatch,
  details: TErrorDetails | undefined,
) => {
  if (typeof details !== "object" || !details.operation_id) {
    return;
  }

  dispatch(
    backgroundOperationStarted({
      operation_id: details.operation_id,
      operation_type: details.operation_type,
    }),
  );
};

const MAX_HEADLINE_LENGTH = 150;

/**
 * True when the failure is a client-side cancel/abort, not a real API error.
 * Axios CancelToken, Axios 1.x CanceledError, and browser XHR aborts all land
 * here with no `error.response`.
 */
export const isCancelledRequest = (error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return true;
  }
  if (!error || typeof error !== "object") {
    return false;
  }
  const err = error as { code?: string; name?: string; message?: string };
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
    return true;
  }
  return (
    typeof err.message === "string" && /request aborted/i.test(err.message)
  );
};

const isAxiosTimeoutWithoutResponse = (error: {
  code?: string;
  message?: string;
}): boolean => {
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return true;
  }
  return typeof error.message === "string" && /timeout/i.test(error.message);
};

const isBrowserOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const getErrorMessage = (error: any, lang: TLang) => {
  if (error.errorStatus === BUSINESS_ERROR.RUN_IN_BACKGROUND) {
    return lang.actions.runInBackgroundMessage;
  }
  // error can be an applicative or a business error.
  const isBusiness = error.hasOwnProperty("errorStatus");
  if (isBusiness) {
    // The backend already translates business error messages: show them as the
    // notification headline instead of a technical "<Action> action failed".
    // Overly long messages (e.g. raw Joi errors embedding the serialized
    // payload) stay in the detail section only.
    const headline = error.serverMessage ?? error.message;
    return _.isEmpty(headline) || headline.length > MAX_HEADLINE_LENGTH
      ? lang.notifications.errorNotifications.internalError
      : headline;
  }

  if (!error.response) {
    // Axios timeout and gateway timeouts without a body used to share the same
    // "are you connected?" copy as a true offline failure (FP-13797).
    if (isAxiosTimeoutWithoutResponse(error)) {
      return lang.notifications.errorNotifications.timeoutError;
    }
    if (isBrowserOffline()) {
      return lang.notifications.errorNotifications.networkError;
    }
    return lang.notifications.errorNotifications.requestUnreachable;
  }

  if (error.response.status === 422) {
    return lang.notifications.errorNotifications.validationError;
  }
  if (error.response.status === 504) {
    return lang.notifications.errorNotifications.timeoutError;
  }

  return lang.notifications.errorNotifications.internalError;
};

const getDetailedErrorMessage = (error: any, actionName?: string) => {
  const errorMessage = error.message ?? "";
  const baseMessage = actionName
    ? `${actionName}: ${errorMessage}`
    : errorMessage;

  const details = error?.response?.data?.data?.error.message;
  const debugContent =
    typeof details === "string" ? details : JSON.stringify(details);

  const divider = `\n⸻⸻⸻\n`;
  const debugMessage = _.isEmpty(details)
    ? ``
    : `\n${divider}Debug info${divider}\n${debugContent}`;

  return `${baseMessage}${debugMessage}`.trim();
};

interface IData<T> {
  code_response: BUSINESS_ERROR;
  data: T;
  error_message: string;
}

export interface IServerResponse<T> {
  data: IData<T>;
}

export interface IOpenWarningModalParams {
  title: string;
  description: string;
  onConfirm?: any;
}

export type TOpenWarningModalFunc = (params: IOpenWarningModalParams) => any;
export const openWarningModal: TOpenWarningModalFunc = (params) => {
  return (dispatch: Dispatch) => {
    dispatch(openWarningModalActionCreator({ ...params }));
  };
};

export const closeWarningModal = () => {
  return (dispatch: Dispatch) => {
    dispatch(closeWarningModalActionCreator());
  };
};

export const extractDataAndCheckErrorStatus = <T extends TErrorDetails>(
  serverResponse: IServerResponse<T>,
): T => {
  // const data = serverResponse && serverResponse.data ? serverResponse.data : serverResponse
  const { data } = serverResponse;
  if (!data.code_response) {
    throw new Error("Server response do not have any 'code_response'");
  } else {
    switch (data.code_response) {
      case BUSINESS_ERROR.SUCCESS: {
        return data.data;
      }
      case BUSINESS_ERROR.RUN_IN_BACKGROUND: {
        // data holds { operation_id, operation_type } of the registered operation
        throw new RunInBackgroundBusinessError(data.data);
      }
      case BUSINESS_ERROR.ALREADY_EXISTS: {
        throw new AlreadyExistsBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.AUTHENTICATION: {
        throw new AuthenticationBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.UNIQUE_FIELD_VIOLATION:
      case BUSINESS_ERROR.NOT_ALLOWED: {
        throw new NotAllowedBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.NOT_FOUND: {
        throw new NotFoundBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.NOT_UP_TO_DATE: {
        throw new NotUpToDateBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.PARTIAL_SUCCESS: {
        throw new PartialFailureBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.QUOTA_EXCEEDED: {
        throw new QuotaExceededBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.PAYLOAD_TOO_LARGE: {
        throw new PayloadTooLargeBusinessError(data.data);
      }
      case BUSINESS_ERROR.VALIDATION_ERROR: {
        throw new ValidationBusinessError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.FREE_TRIAL_LIMIT_REACHED: {
        throw new FreeTrialLimitReachedError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.TIMEOUT: {
        throw new TimeoutError(data.error_message, data.data);
      }
      case BUSINESS_ERROR.SQL_ERROR: {
        throw new SQLBusinessError(
          data.error_message,
          formatSQLString(data.data),
        );
      }
      default: {
        throw new Error(
          "Server response do not have any 'code_response' recognized",
        );
      }
    }
  }
};

// this is a copy of what is done in the backend. Maybe it's better to move it in fieldpro-tools one day,
// but let's leave it as such for now, as we might change the shape of this object in the near future.
export interface IGenericQueryToSendToBackend<
  TModel extends Record<string, unknown>,
> {
  filters: Partial<{
    [K in keyof TModel]: TAny;
  }> & {
    _active?: boolean | boolean[];
    _id?: Array<string> | string;
    _updated_at?: string;
    _updated_by?: string;
    _created_at?: string;
    _created_by?: string;
  };
  limit?: number;
  updated_after?: Date;
  updated_before?: Date;
  start_after?: string;
  offset?: number;
  teams?: string[];
  only_duplicated?: string;
  order_by?: string;
  order?: "asc" | "desc" | "";
  with_count?: boolean;
  include_linked_options?: boolean;
  ignore_scope?: boolean;
  search?: string;
  detail?: boolean;
  item_id?: string;
  tag?: string;
  sku_group_id?: string;
}

export interface IAdditionalQueryOption {
  eraseExisting?: boolean;
  skipStoreUpdate?: boolean;
  skipBackendCall?: boolean;
  storeAsTableItems?: boolean;
  clearExistingTableItems?: boolean;
}
